import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../api';
import { SEGMENTS_CHANGED_EVENT, announceSegmentsChanged, rulingLabel } from '../../hooks/useDocumentSegments';
import SegMenu from '../segments/SegMenu';
import SegInlineEdit from '../segments/SegInlineEdit';
import UndoToast, { useUndoToast } from '../segments/UndoToast';
import { fmtType } from '../segments/segmentTypes';
import { pagesRange } from '../segments/cutChild';
import { useBackgroundJobs } from '../jobs/BackgroundJobsContext';
import ExportModal from './ExportModal';

// "Documents in this file" — the per-file list in the inventory row for a
// compilation PDF (plans/BY_DOCUMENT_INVENTORY_VIEW_PROPOSAL.md §3.4, §7c, §7d).
//
// Automatic first (owner 2026-09-30, §7d): the detector's documents are simply
// listed — index, title, type, pages — with no chips and nothing to approve.
// Corrections live in the hover "⋯" menu on each row (Merge with previous ·
// Rename · Change type · Move start… · Split…) and every one answers with an
// Undo toast; the file keeps an "Edits (n)" history. The only time the panel
// asks is when the system itself was unsure (a file warning, "?" on a row).
// The list is the ledger; the file is cut into real documents by a background
// job after detection and after every correction (`data.cut`), and the panel
// says "Updating…" only while that job has work to do.
const POLL_MS = 5000;
const POLL_MAX_MS = 6 * 60 * 1000;
const MIN_PAGES = 20;                                // the detector looks at PDFs of 20 pages and more

const EDIT_LABEL = {
  dismiss: (e) => `Merged the document at p.${e.page} into the one above`,
  merge: (e) => `Merged the document at p.${e.page} into the one above`,
  add: (e) => `Document starts on p.${e.page}`,
  split: (e) => `Document starts on p.${e.page}`,
  move: (e) => `Start moved p.${e.page} → p.${e.to_page}`,
  rename: (e) => `Renamed the document at p.${e.page} to “${String(e.value || '').slice(0, 40)}”`,
  retype: (e) => `Type of the document at p.${e.page} → ${fmtType(e.value)}`,
  accept: (e) => `Kept the document at p.${e.page} separate`,
};
const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export default function DetectedDocumentsSection({ doc, onShowViewer }) {
  const docId = doc?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);       // {text, error}
  const [busy, setBusy] = useState(null);           // action key in flight
  const [listOpen, setListOpen] = useState(true);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [editsOpen, setEditsOpen] = useState(false);
  const [editing, setEditing] = useState(null);     // {page, kind:'move'|'rename'|'retype'}
  const [addOpen, setAddOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const { toast, show: showToast, clear: clearToast } = useUndoToast();
  const runRef = useRef(false);                      // synchronous double-click guard
  const pollRef = useRef(null);
  const { jobs } = useBackgroundJobs();
  const [ruledAt, setRuledAt] = useState(0);         // when a correction was last made here

  const load = useCallback(async () => {
    if (!docId) return null;
    return api.get(`/documents/${docId}/segments`);
  }, [docId]);

  // one-shot load (StrictMode double-run safe; never depends on state it sets)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load().then((d) => { if (cancelled) return; setData(d); setLoading(false); })
      .catch((e) => { if (cancelled) return; setLoading(false); setStatus({ text: e?.message || 'Could not load the documents in this file', error: true }); });
    return () => { cancelled = true; if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };
  }, [load]);

  // a correction made in the viewer or the gallery refreshes this list
  useEffect(() => {
    const onChanged = (e) => {
      if (e?.detail?.documentId !== docId || e?.detail?.source === 'panel') return;
      load().then((d) => setData(d)).catch(() => {});
    };
    window.addEventListener(SEGMENTS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(SEGMENTS_CHANGED_EVENT, onChanged);
  }, [docId, load]);

  // poll after a run until the detector has written its result
  const pollUntilAvailable = useCallback((startedAt, previousDetectedAt) => {
    if (pollRef.current) clearTimeout(pollRef.current);
    const tick = async () => {
      try {
        const d = await load();
        const fresh = d?.available && d?.detected_at && d.detected_at !== previousDetectedAt;
        if (fresh) {
          setData(d);
          setStatus({ text: d.segment_count > 1 ? `Found ${d.segment_count} documents` : 'One document — nothing to split', error: false });
          runRef.current = false;
          return;
        }
        setData((prev) => ({ ...(prev || {}), ...(d || {}), running: true }));
      } catch (e) { /* transient: keep polling */ }
      if (Date.now() - startedAt > POLL_MAX_MS) {
        setStatus({ text: 'Still running in the background — reopen this file in a few minutes.', error: false });
        runRef.current = false;
        setData((prev) => (prev ? { ...prev, running: false } : prev));
        return;
      }
      pollRef.current = setTimeout(tick, POLL_MS);
    };
    pollRef.current = setTimeout(tick, POLL_MS);
  }, [load]);

  async function handleRun(force = false) {
    if (runRef.current) return;
    runRef.current = true;
    setBusy('run');
    setStatus({ text: 'Finding documents in this file…', error: false });
    const previousDetectedAt = data?.detected_at || null;
    try {
      const r = await api.post(`/documents/${docId}/segments/run`, { force });
      if (r?.result) {
        setData(r.result);
        setStatus({ text: r.result.segment_count > 1 ? `Found ${r.result.segment_count} documents` : 'One document — nothing to split', error: false });
        runRef.current = false;
      } else {
        setData((prev) => ({ ...(prev || {}), running: true }));
        pollUntilAvailable(Date.now(), previousDetectedAt);
      }
    } catch (e) {
      setStatus({ text: e?.message || 'Could not start detection', error: true });
      runRef.current = false;
    } finally {
      setBusy(null);
    }
  }

  async function rule(action, page, extra = {}, seg = null) {
    const key = `${action}:${page}`;
    if (busy) return;
    setBusy(key);
    setStatus(null);
    try {
      const d = await api.post(`/documents/${docId}/segments/decisions`, { action, page, ...extra });
      setData(d);
      setRuledAt(Date.now());
      announceSegmentsChanged(docId, 'panel');
      setEditing(null);
      setAddOpen(false);
      showToast(rulingLabel(action, page, extra, seg));
    } catch (e) {
      setStatus({ text: e?.message || `Could not ${action}`, error: true });
    } finally {
      setBusy(null);
    }
  }

  async function undo() {
    if (busy) return;
    setBusy('undo');
    setStatus(null);
    try {
      const d = await api.post(`/documents/${docId}/segments/decisions/undo`, {});
      setData(d);
      setRuledAt(Date.now());
      announceSegmentsChanged(docId, 'panel');
      clearToast();
    } catch (e) {
      setStatus({ text: e?.message || 'Could not undo', error: true });
    } finally {
      setBusy(null);
    }
  }

  function openAt(page) { onShowViewer?.(docId, null, page); }

  if (!docId) return null;
  if (loading && !data) {
    // say "Loading…" only for a file that can hold documents (a cut file, or a PDF long
    // enough to be checked); for everything else the section never shows, so it must not flash
    const likely = doc.is_compilation_container || (doc.extraction?.page_count || 0) >= MIN_PAGES;
    if (!likely) return null;
    return (
      <div className="inv-detail-segments">
        <span className="inv-detail-label">Documents in this file</span>
        <span className="inv-tier-msg">Loading…</span>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="inv-detail-segments">
        <span className="inv-detail-label">Documents in this file</span>
        {status && <span className={`inv-tier-msg ${status.error ? 'error' : ''}`}>{status.text}</span>}
      </div>
    );
  }
  if (!data.eligible && !data.available) return null;   // not a PDF, or too short, and never analysed: say nothing

  const segs = data.segments || [];
  const sections = data.sections || [];
  const edits = data.edits || [];
  const warnings = data.file_warnings || [];
  const unsure = data.unsure_pages || [];
  // "Updating…" only while a re-cut is really on its way: the rows disagree with this list
  // AND the job is queued / running (or the correction is seconds old). Never a standing label.
  const cutJobActive = (jobs || []).some((j) => j.job_type === 'materialize_segments' && j.params?.document_id === docId
    && ['queued', 'processing'].includes(String(j.status || '').toLowerCase()));
  const updating = data.cut?.state === 'updating' && (cutJobActive || Date.now() - ruledAt < 45_000);

  // the file's documents exist as their own files once it is cut: that is what a package holds
  const canExport = !!doc.is_compilation_container && segs.length > 1 && !!doc.case_id;
  const headerMenu = data.available ? [
    ...(canExport ? [{ key: 'export', label: '⬇ Export these documents…', title: 'A zip with each document as its own file and an index page that opens in a browser', onSelect: () => setExportOpen(true) }] : []),
    { key: 'add', label: '＋ A document starts on page…', title: 'Draw a boundary the detector missed', onSelect: () => setAddOpen(true) },
    { key: 'redetect', label: data.running ? 'Running…' : '↻ Find documents again', disabled: !!busy || data.running,
      title: 'Run the detector again. Your corrections are kept and re-applied.', onSelect: () => handleRun(true) },
  ] : [];

  return (
    <div className="inv-detail-segments" onClick={(e) => e.stopPropagation()}>
      <div className="inv-seg-head seg-hover-host">
        <span className="inv-detail-label">Documents in this file</span>
        {data.available ? (
          <>
            <span className="inv-seg-summary" data-testid="seg-summary">
              {segs.length === 1 ? 'One document — the whole file' : `${segs.length} documents`}
              {data.truncated ? ' · first 600 pages only' : ''}
            </span>
            {updating && (
              <span className="inv-seg-updating" data-testid="seg-updating" title="The file's documents are being brought in line with this list (only the ones that changed are cut again)">Updating…</span>
            )}
            {segs.length > 1 && (
              <button className="btn btn-sm btn-ghost" onClick={() => setListOpen(!listOpen)}>{listOpen ? '▾ Hide' : '▸ Show'}</button>
            )}
            {canExport && (
              <button
                className="btn btn-sm btn-ghost" data-testid="seg-export" disabled={updating}
                onClick={() => setExportOpen(true)}
                title={updating ? 'The documents are being updated — export in a moment' : 'A zip with each document as its own file and an index page that opens in a browser'}
              >⬇ Export</button>
            )}
            <SegMenu items={headerMenu} title="More" align="left" />
            {exportOpen && (
              <ExportModal
                caseId={doc.case_id} scope="file" documentId={docId} count={segs.length}
                label="the documents in this file" onClose={() => setExportOpen(false)}
              />
            )}
          </>
        ) : null}
      </div>

      {/* A long PDF nobody has looked inside yet: say so plainly and offer the check where it is seen
          (Denise, 2026-10-13: "I was unable to figure out how to … run the inventory") */}
      {!data.available && data.eligible && (
        <div className="seg-callout" data-testid="seg-callout">
          <span className="seg-callout-icon" aria-hidden="true">🔍</span>
          <div className="seg-callout-body">
            <div className="seg-callout-text">
              {data.page_count ? `This PDF has ${data.page_count} pages. ` : ''}
              Chargestack has not looked for separate documents inside it yet — reports, statements and letters
              stapled together each become a document of their own, with its own title, summary and pages.
            </div>
            <div className="seg-callout-actions">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => handleRun(false)}
                disabled={!!busy || data.running}
                title="Look for separate documents inside this PDF (reports, statements, letters stapled together)."
              >
                {data.running ? 'Finding documents…' : 'Find documents in this file'}
              </button>
              <span className="seg-callout-cost">A paid reading pass — typically a few cents for a long PDF.</span>
            </div>
          </div>
        </div>
      )}

      {(warnings.length > 0 || unsure.length > 0) && (
        <div className="seg-warn" data-testid="seg-warnings">
          <span>⚠</span>
          <span>
            {warnings.map((w) => w.text).join(' · ')}
            {warnings.length > 0 && unsure.length > 0 ? ' · ' : ''}
            {unsure.length > 0 ? `Not sure about ${unsure.length} — marked ? below; open one to check, or merge it if it is a continuation.` : ''}
          </span>
        </div>
      )}

      {addOpen && data.available && (
        <SegInlineEdit kind="move" value="" pageCount={data.page_count} busy={!!busy}
          onCancel={() => setAddOpen(false)} onSave={({ to_page }) => rule('add', to_page)} />
      )}

      {data.available && listOpen && segs.length > 1 && (
        <ol className="inv-seg-list">
          {segs.map((s, i) => {
            const isEditing = editing && editing.page === s.page_start ? editing : null;
            const menu = [
              s.page_start > 1 && { key: 'merge', label: '⤒ Merge with previous', danger: true, title: 'Not a separate document — these pages belong to the one above',
                onSelect: () => rule('dismiss', s.page_start, {}, s) },
              { key: 'rename', label: '✎ Rename', onSelect: () => setEditing({ page: s.page_start, kind: 'rename' }) },
              { key: 'retype', label: '⌥ Change type', onSelect: () => setEditing({ page: s.page_start, kind: 'retype' }) },
              s.page_start > 1 && { key: 'move', label: '↔ Move start…', title: 'The document really starts on a different page',
                onSelect: () => setEditing({ page: s.page_start, kind: 'move' }) },
              { key: 'split', label: '✂ Split…', title: 'Open the file here and mark where another document starts', onSelect: () => openAt(s.page_start) },
            ];
            return (
              <li key={s.page_start} className="inv-seg-row seg-hover-host" data-seg-page={s.page_start}>
                <div className="inv-seg-plain">
                  <span className="inv-seg-idx">{i + 1}</span>
                  {s.unsure && <span className="seg-unsure" title="The detector was not sure a document starts here">?</span>}
                  <span className="inv-seg-title" title="Open the file at this page" onClick={() => openAt(s.page_start)}>
                    {s.title || fmtType(s.doc_type) || <em className="inv-seg-untitled">untitled</em>}
                  </span>
                  <span className="inv-seg-meta">
                    {s.doc_type && s.title ? `${fmtType(s.doc_type)} · ` : ''}
                    <button className="inv-seg-pages" onClick={() => openAt(s.page_start)} title="Open the file at this page">{pagesRange(s.page_start, s.page_end)}</button>
                    {' '}({s.page_count} {s.page_count === 1 ? 'page' : 'pages'})
                  </span>
                  <SegMenu items={menu} />
                </div>
                {isEditing && (
                  <SegInlineEdit
                    kind={isEditing.kind} busy={!!busy} pageCount={data.page_count}
                    value={isEditing.kind === 'rename' ? (s.title || '') : isEditing.kind === 'retype' ? (s.doc_type || '') : s.page_start}
                    onCancel={() => setEditing(null)}
                    onSave={(extra) => rule(isEditing.kind, s.page_start, extra, s)}
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}

      {data.available && (
        <div className="inv-seg-foot">
          {edits.length > 0 && (
            <div className="inv-seg-edits" data-testid="seg-edits">
              <button className="btn btn-sm btn-ghost" onClick={() => setEditsOpen(!editsOpen)}>
                {editsOpen ? '▾' : '▸'} Edits ({edits.length})
              </button>
              <button className="btn btn-sm btn-ghost" disabled={!!busy} onClick={undo} title="Reverse the most recent edit">↶ Undo last</button>
              {editsOpen && (
                <ul>
                  {edits.slice(0, 20).map((e) => (
                    <li key={e.id || `${e.action}:${e.page}:${e.at}`}>
                      <time>{fmtWhen(e.at)}</time>
                      <span>{(EDIT_LABEL[e.action] || (() => e.action))(e)}</span>
                      {e.by && <span className="inv-seg-ruled">{e.by}</span>}
                    </li>
                  ))}
                  {edits.length > 20 && <li><span className="inv-tier-msg">… {edits.length - 20} more</span></li>}
                </ul>
              )}
            </div>
          )}
          {sections.length > 0 && (
            <div className="inv-seg-sections">
              <button className="btn btn-sm btn-ghost" onClick={() => setSectionsOpen(!sectionsOpen)} title="Labelled parts inside one document (Supplement, Narrative, chapters). Navigation only — never cut.">
                {sectionsOpen ? '▾' : '▸'} {sections.length} {sections.length === 1 ? 'section' : 'sections'} inside (not cut)
              </button>
              {sectionsOpen && (
                <div className="inv-seg-section-list">
                  {sections.slice(0, 120).map((sec) => (
                    <button key={sec.page} className="inv-seg-pages" onClick={() => openAt(sec.page)} title={sec.title || ''}>
                      p. {sec.page}{sec.title ? ` ${String(sec.title).slice(0, 28)}` : ''}
                    </button>
                  ))}
                  {sections.length > 120 && <span className="inv-tier-msg">… {sections.length - 120} more</span>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {status && <span className={`inv-tier-msg ${status.error ? 'error' : ''}`}>{status.text}</span>}
      <UndoToast toast={toast} busy={!!busy} onUndo={undo} onClose={clearToast} />
    </div>
  );
}
